import SettingRow from '@components/Customization/AppSettings/SettingRow';
import ScopePicker, { ORGANIZATION } from '@components/Customization/ScopePicker';
import Header from '@components/Shared/Layout/Header';
import {
  applyDraft, type AppSettingsDraft, formatSettingValue, groupLabelKey, lineColorKeyFor, pendingOf, resolveSetting,
  sectionLabelKey, sectionOf, sectionsOf, settingLabelKey, stageReset, stageValue,
} from '@lib/customization/appSettings';
import { landingOwner } from '@lib/customization/landingOwner';
import { keptDraftsOf, useAskBeforeLeaving } from '@lib/customization/unsavedWork';
import { useLocalization } from '@lib/localization';
import {
  Box, Button, IconButton, InputBase, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import {
  buttonStyles, changedPillStyles, countPillStyles, filterChipActiveStyles, filterChipStyles, groupHeaderStyles,
  groupTabActiveStyles, groupTabsFadeStyles, groupTabsStyles, groupTabStyles, heroIconStyles, pendingDotStyles,
  readOnlyBannerStyles, saveBarStyles, searchActiveStyles, searchHintStyles, searchStyles, sectionHeaderStyles, tileStyles,
  toolbarClusterStyles, toolbarStyles,
} from '@pages/AppSettings.styles';
import { dangerHoverStyles } from '@pages/CustomPoiTypes.styles';
import {
  type AppSettingDefinition, type AppSettingGroup, type AppSettingValue, useGetAppSettingsQuery, useGetAppSettingsSchemaQuery,
  usePatchAppSettingsMutation,
} from '@store/api/appSettingsEndpoints';
import { useGetTeamsQuery } from '@store/api/teamEndpoints';
import { useAppSelector, usePermission } from '@store/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CogIcon, CrosshairsIcon, FileAltIcon, LockIcon, MapMarkerIcon, MobileAltIcon, PaletteIcon, SearchIcon, SlidersHIcon,
  TimesIcon, UndoAltIcon, UniversalAccessIcon,
} from 'react-line-awesome';
import { useSearchParams } from 'react-router-dom';

const groupIcons: Record<string, React.ReactNode> = {
  general: <CogIcon />,
  graphics: <PaletteIcon />,
  tracking: <CrosshairsIcon />,
  poi: <MapMarkerIcon />,
  accessibility: <UniversalAccessIcon />,
  report: <FileAltIcon />,
};


/**
 * Unsaved changes by what they are for — a team's id, or the organization — for as long as the
 * tab lives. Opening another team or leaving the page unmounts what held them, and the router
 * cannot ask first; kept here, they are there again on the way back. They are their author's
 * alone: signing out does not reload the tab, so whoever signs in next starts with none. A
 * reload or a close of the tab asks first while one of them still holds something to save.
 */
const keptDrafts = keptDraftsOf<AppSettingsDraft>('app-settings');

/**
 * The SuPAR app's default settings in two layers: the organization's (`?scope=organization`),
 * which every team starts from, and one team's (`?team=`) on top of them — each shown over
 * what is beneath it. With neither in the address the page opens where `landingOwner` says:
 * the organization for whoever may change it, the reader's default team for everybody else.
 *
 * Nothing is written until Save. A control changes a draft, the page shows the settings as
 * saving it would leave them, and Save sends the whole draft as one patch: only the settings
 * that changed, so what somebody else saved under another setting meanwhile is not undone.
 */
const AppSettingsPage = () => {
  const { t, currentLanguage } = useLocalization();
  const { can, canInAnyTeam, userId } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamId = searchParams.get('team') ?? undefined;

  const { data: schema, isLoading: isSchemaLoading, isError: isSchemaError } = useGetAppSettingsSchemaQuery();
  const { data: teams, isLoading: isTeamsLoading } = useGetTeamsQuery();
  const { data: settings, isLoading: isSettingsLoading, isError: isSettingsError } = useGetAppSettingsQuery();
  const [patchAppSettings, { isLoading: isSaving }] = usePatchAppSettingsMutation();

  // Whoever may read a team's settings may read the organization's: they are what that team's
  // sit on. Changing them takes the permission for every team, which no team role gives.
  const canReadOrganization = canInAnyTeam('app_settings:get');
  const isOrganization = canReadOrganization && searchParams.get('scope') === ORGANIZATION;

  const readableTeams = useMemo(() => (teams ?? []).filter((team) => can('app_settings:get', team._id)), [teams, can]);
  const team = isOrganization ? undefined : readableTeams.find((candidate) => candidate._id === teamId);
  const scope = isOrganization ? ORGANIZATION : team?._id;

  // Neither in the address, or a team this reader cannot open — a stale link, a team since
  // deleted — lands where the page would open by itself, instead of on an empty page.
  const defaultTeamId = useAppSelector((state) => state.auth.user?.defaultTeam?.id);
  const landing = useMemo(() => landingOwner({
    canEditOrganization: can('app_settings:patch'),
    canReadOrganization,
    defaultTeamId,
    teamIds: readableTeams.map((candidate) => candidate._id),
  }), [can, canReadOrganization, defaultTeamId, readableTeams]);

  useEffect(() => {
    if (isTeamsLoading || scope || landing === undefined) return;

    setSearchParams(landing === null ? { scope: ORGANIZATION } : { team: landing }, { replace: true });
  }, [isTeamsLoading, scope, landing, setSearchParams]);

  // What is set in the settings on screen, and what applies where they set nothing: under a
  // team the organization's values, under the organization only the app's defaults.
  const ownValues = useMemo(
    () => (isOrganization ? settings?.organization.values : team && settings?.teams[team._id]?.values) ?? {},
    [isOrganization, team, settings],
  );
  const beneathValues = useMemo(
    () => (isOrganization ? {} : settings?.organization.values ?? {}), [isOrganization, settings],
  );
  const canEditScope = (id: string) => (id === ORGANIZATION
    ? can('app_settings:patch')
    : readableTeams.some((candidate) => candidate._id === id) && can('app_settings:patch', id));
  const canEdit = Boolean(scope) && canEditScope(scope ?? '');

  const isLoading = isSchemaLoading || isSettingsLoading;
  // Without the catalogue there is nothing to list, and without the settings every control
  // would show the default, hiding what was set. A re-read that fails leaves what was read.
  const isLoadFailed = (isSchemaError && !schema) || (isSettingsError && !settings);

  const [drafts, setDrafts] = useState(() => keptDrafts.readAll(userId));

  // A draft counts only where it can still be saved: not for a team since deleted, nor for
  // settings the reader may no longer change.
  const savedValuesOf = (id: string) => (id === ORGANIZATION ? settings?.organization.values : settings?.teams[id]?.values) ?? {};
  const hasUnsaved = (id: string) =>
    canEditScope(id) && Object.keys(pendingOf(drafts[id] ?? {}, savedValuesOf(id))).length > 0;
  // As one text, so that the effect below runs when the answer changes and not with every render.
  const unsavedIds = Object.keys(drafts).filter(hasUnsaved).join();
  const anyUnsaved = unsavedIds !== '';

  // Every draft is kept as it is, with whether it holds something to save. That is decided
  // here, against the saved settings and what the reader may change, which nothing can look
  // up once the page has been left. Until what a draft is judged against has been read the
  // page cannot tell either, and what was said of the draft before stands: a reader who is
  // back and leaves again before it arrives is still asked about what they left.
  //
  // The organization's draft is judged against the settings alone, and only a team's waits
  // for the teams as well. A visit in which the teams could not be read still changes and
  // saves the organization's settings, and a draft that changes while nothing is said of it
  // keeps a flag that was true of another state: asked about after its save, or not asked
  // about after the change that followed. An empty draft waits for nothing: it holds nothing
  // to save whatever was read, and it is what a save or a discard leaves behind.
  const settingsRead = Boolean(settings);
  const teamsRead = Boolean(teams);

  useEffect(() => {
    const unsaved = unsavedIds.split(',');

    const holdsUnsaved = (id: string, kept: AppSettingsDraft): boolean | undefined => {
      if (Object.keys(kept).length === 0) return false;

      const judged = settingsRead && (id === ORGANIZATION || teamsRead);

      return judged ? unsaved.includes(id) : undefined;
    };

    Object.entries(drafts).forEach(([id, kept]) => keptDrafts.keep(userId, id, kept, holdsUnsaved(id, kept)));
  }, [userId, drafts, unsavedIds, settingsRead, teamsRead]);

  // A save can end after the page was left; it then has to say so where the drafts are kept.
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  // What is not saved yet for the settings on screen, and those settings with it applied: what
  // every row, count and filter below shows.
  const draft = useMemo(
    () => (canEdit ? pendingOf((scope && drafts[scope]) || {}, ownValues) : {}),
    [canEdit, scope, drafts, ownValues],
  );
  const shownValues = useMemo(() => applyDraft(ownValues, draft), [ownValues, draft]);
  const pendingCount = Object.keys(draft).length;

  // Reloading or closing the tab is the one way a draft is lost, so that asks first: here for
  // the page on screen. Once the page has been left the same is asked for the drafts it kept.
  useAskBeforeLeaving(anyUnsaved);

  const [search, setSearch] = useState('');
  const [changedOnly, setChangedOnly] = useState(false);

  // A setting outside any group is drawn as a group of its own.
  const groups = useMemo(() => (schema ?? []).map((item): AppSettingGroup => (
    item.kind === 'group' ? item : { kind: 'group', id: item.key, fields: [item] }
  )), [schema]);
  const definitions = useMemo(() => groups.flatMap((group) => group.fields), [groups]);
  const definitionByKey = useMemo(() => new Map(definitions.map((definition) => [definition.key, definition])), [definitions]);

  const resolve = (definition: AppSettingDefinition) => resolveSetting(definition, shownValues, beneathValues);

  const setCount = Object.keys(shownValues).length;

  // The filter shows only what is set here, so with nothing set it could only ever show an
  // empty page — its chip is hidden then, and a reset that empties these settings turns it off.
  useEffect(() => {
    if (setCount === 0) setChangedOnly(false);
  }, [setCount]);

  // `/` jumps to the search from anywhere on the page that is not already taking text.
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.target instanceof HTMLElement && event.target.closest('input, textarea, select, [contenteditable="true"]')) return;

      event.preventDefault();
      searchRef.current?.focus();
    };

    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, []);

  const matches = (definition: AppSettingDefinition, groupId: string) => {
    if (changedOnly && shownValues[definition.key] === undefined) return false;

    const query = search.trim().toLocaleLowerCase(currentLanguage);
    if (!query) return true;

    const section = sectionOf(definition.key);
    return [t(settingLabelKey(definition.key)), t(groupLabelKey(groupId)), section ? t(sectionLabelKey(section)) : '']
      .some((text) => text.toLocaleLowerCase(currentLanguage).includes(query));
  };

  const visibleGroups = groups
    .map((group) => ({ ...group, fields: group.fields.filter((definition) => matches(definition, group.id)) }))
    .filter((group) => group.fields.length > 0);
  const visibleCount = visibleGroups.reduce((total, group) => total + group.fields.length, 0);

  const stage = (change: (current: AppSettingsDraft) => AppSettingsDraft) => {
    if (scope) setDrafts((all) => ({ ...all, [scope]: change(all[scope] ?? {}) }));
  };

  // Choosing the value the setting would fall back to — the organization's under a team, else
  // the app's default — is taking it off, staged as `null`, and the row is back on that value
  // at once; the server takes it off the same way. Any other value is set, the app's default
  // included where the organization set something else.
  const setValue = (key: string, value: AppSettingValue | null) => {
    const definition = definitionByKey.get(key);
    if (!definition) return;

    const isNoChange = value === resolve(definition).fallback;
    stage((current) => stageValue(current, ownValues, key, isNoChange ? null : value));
  };

  // Everything shown as set is taken off, as unsaved as any other change: nothing goes until
  // Save, and Discard brings it all back.
  const resetSettings = () => stage((current) => stageReset(current, ownValues));

  const discard = () => stage(() => ({}));

  const save = async () => {
    if (!scope || pendingCount === 0) return;

    const sent = draft;
    const result = await patchAppSettings({ teamId: team?._id ?? null, values: sent });
    if (result.error) return;

    // What was sent is saved; a choice made while it was on its way stays unsaved.
    const unsent = (staged: AppSettingsDraft = {}): AppSettingsDraft =>
      Object.fromEntries(Object.entries(staged).filter(([key, value]) => sent[key] !== value));

    setDrafts((all) => ({ ...all, [scope]: unsent(all[scope]) }));

    // A save that ends after the page was left finds no effect to keep its outcome, so it
    // takes what it saved out of the kept draft itself. Left in, it would be asked about
    // before a reload although nothing of it is unsaved.
    if (!alive.current) {
      const left = unsent(keptDrafts.read(userId, scope));

      keptDrafts.leave(userId, scope, left, Object.keys(left).length > 0);
    }
  };

  /** Why a setting has no effect right now, when another one decides that. */
  const dormantHint = (definition: AppSettingDefinition): string | undefined => {
    const condition = definition.appliesWhen;
    const conditionDefinition = condition && definitionByKey.get(condition.key);
    if (!condition || !conditionDefinition) return undefined;
    if (resolve(conditionDefinition).value === condition.value) return undefined;

    return t('AppSettings_AppliesWhen', [
      t(settingLabelKey(condition.key)),
      formatSettingValue(conditionDefinition, condition.value, t, currentLanguage),
    ]);
  };

  // (The two effects below also wait on what is open: the page renders without its toolbar
  // until a team or the organization is, so their refs only exist from then on.)
  //
  // Which group the reader is in, for the tabs over the list: the last one whose card has
  // reached the bottom of the sticky toolbar, the first one before any has. A page scrolled
  // to its end answers with its last group, which may be too short to ever reach the line.
  const toolbarRef = useRef<HTMLDivElement>(null);
  const groupElements = useRef(new Map<string, HTMLElement>());
  const [activeGroup, setActiveGroup] = useState<string>();
  const visibleGroupIds = visibleGroups.map((group) => group.id).join();

  useEffect(() => {
    const scroller = toolbarRef.current?.closest('main');
    const ids = visibleGroupIds.split(',').filter(Boolean);
    if (!scroller || ids.length === 0) return undefined;

    const update = () => {
      const line = (toolbarRef.current?.getBoundingClientRect().bottom ?? 0) + 24;
      const atEnd = scroller.scrollTop > 0 && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4;
      const reached = ids.filter((id) => (groupElements.current.get(id)?.getBoundingClientRect().top ?? Infinity) <= line);

      setActiveGroup(atEnd ? ids[ids.length - 1] : reached[reached.length - 1] ?? ids[0]);
    };

    update();
    scroller.addEventListener('scroll', update, { passive: true });

    return () => scroller.removeEventListener('scroll', update);
  }, [visibleGroupIds, isLoading, scope]);

  // A tab brings its card to just below the toolbar, inside the line above that counts it as
  // reached, so the tab clicked is the one that lights up. Measured where the toolbar's bottom
  // will be once it sticks: from the top of the page it has not stuck yet.
  const scrollToGroup = (groupId: string) => {
    const toolbar = toolbarRef.current;
    const card = groupElements.current.get(groupId);
    const scroller = toolbar?.closest('main');
    if (!toolbar || !card || !scroller) return;

    const stuckBottom = scroller.getBoundingClientRect().top + parseFloat(getComputedStyle(toolbar).top) + toolbar.offsetHeight;
    scroller.scrollBy({ top: card.getBoundingClientRect().top - stuckBottom - 12, behavior: 'smooth' });
  };

  // The tabs fade out at the right edge only while some of them are scrolled out of view.
  const tabsRef = useRef<HTMLDivElement>(null);
  const [tabsOverflow, setTabsOverflow] = useState(false);
  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return undefined;

    const measure = () => setTabsOverflow(tabs.scrollWidth > tabs.clientWidth + 1);
    const observer = new ResizeObserver(measure);

    observer.observe(tabs);
    measure();

    return () => observer.disconnect();
  }, [visibleGroupIds, isLoading, scope]);

  const nothingChangedKey = isOrganization ? 'AppSettings_NothingChanged_Organization' : 'AppSettings_NothingChanged';

  const changedIn = (fields: AppSettingDefinition[]) =>
    fields.filter((definition) => shownValues[definition.key] !== undefined).length;

  // Nothing open yet: on its way to a team or the organization, or a reader who may open neither.
  if (!scope) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        {isTeamsLoading || landing !== undefined ? (
          <LinearProgress sx={{ width: '100%' }} />
        ) : (
          <Paper variant="outlined">
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>{t('AppSettings_NoTeams')}</Typography>
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ p: 2.5 }} alignItems={{ md: 'flex-start' }}>
          <Stack direction="row" spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={heroIconStyles}><MobileAltIcon /></Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{t('AppSettings_Title')}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 620 }}>
                {t(isOrganization ? 'AppSettings_Subtitle_Organization' : 'AppSettings_Subtitle')}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
              {t(canEdit ? 'AppSettings_Editing' : 'AppSettings_Viewing')}
            </Typography>
            <ScopePicker
              value={scope}
              teams={readableTeams}
              defaultTeamId={defaultTeamId}
              hasUnsaved={hasUnsaved}
              onChange={(next) => setSearchParams(next === ORGANIZATION ? { scope: ORGANIZATION } : { team: next })}
            />
            {/* Beside the picker, not in the toolbar: it takes off everything set for what is chosen
                here. Disabled rather than hidden while nothing is set: appearing with the first
                setting made the card taller and moved the whole list under it. */}
            {canEdit && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  size="small"
                  startIcon={<UndoAltIcon />}
                  disabled={setCount === 0 || isLoadFailed}
                  onClick={resetSettings}
                  sx={{ ...buttonStyles, ...dangerHoverStyles, fontSize: 13 }}
                >
                  {t('AppSettings_ResetLayer', [setCount])}
                </Button>
              </Box>
            )}
          </Box>
        </Stack>
      </Paper>

      {!canEdit && (
        <Stack direction="row" sx={{ ...readOnlyBannerStyles, mb: 2.5 }}>
          <LockIcon />
          <span>{t(isOrganization ? 'AppSettings_ReadOnly_Organization' : 'AppSettings_ReadOnly_Team')}</span>
        </Stack>
      )}

      {/* One row held at the top while the list scrolls under it: where to go on the left, how
          to narrow the list on the right. */}
      <Stack direction="row" ref={toolbarRef} sx={toolbarStyles}>
        <Stack
          direction="row"
          role="tablist"
          ref={tabsRef}
          sx={[groupTabsStyles, tabsOverflow && groupTabsFadeStyles]}
        >
          {!isLoading && visibleGroups.map((group) => {
            const changed = changedIn(group.fields);
            const isActive = activeGroup === group.id;

            return (
              <Stack
                key={group.id}
                direction="row"
                role="tab"
                aria-selected={isActive}
                sx={[groupTabStyles, isActive && groupTabActiveStyles]}
                onClick={() => scrollToGroup(group.id)}
              >
                <span>{t(groupLabelKey(group.id))}</span>
                {changed > 0 && <Box component="span" sx={changedPillStyles}>{changed}</Box>}
              </Stack>
            );
          })}
        </Stack>

        <Stack direction="row" sx={toolbarClusterStyles}>
          {setCount > 0 && !isLoadFailed && (
            <Stack
              direction="row"
              role="switch"
              aria-checked={changedOnly}
              tabIndex={0}
              onClick={() => setChangedOnly((current) => !current)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setChangedOnly((current) => !current);
                }
              }}
              sx={[filterChipStyles, changedOnly && filterChipActiveStyles]}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main', flexShrink: 0 }} />
              <span>{t('AppSettings_SetHere')}</span>
              <Box component="span" sx={changedOnly ? changedPillStyles : countPillStyles}>{setCount}</Box>
            </Stack>
          )}

          <Box sx={[searchStyles, Boolean(search) && searchActiveStyles]}>
            <SearchIcon />
            <InputBase
              inputRef={searchRef}
              value={search}
              placeholder={t('AppSettings_Search')}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setSearch('');
                  searchRef.current?.blur();
                }
              }}
              inputProps={{ 'aria-label': t('AppSettings_Search') }}
              sx={{ flexGrow: 1, fontSize: 13.5, '& input': { p: 0 } }}
            />
            {search ? (
              <>
                <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>
                  {visibleCount}
                </Typography>
                <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25, color: 'text.secondary' }}>
                  <TimesIcon />
                </IconButton>
              </>
            ) : (
              <Box component="kbd" className="search-hint" sx={searchHintStyles}>/</Box>
            )}
          </Box>
        </Stack>
      </Stack>

      {isLoading && <LinearProgress sx={{ width: '100%', my: 2 }} />}

      {!isLoading && isLoadFailed && (
        <Paper variant="outlined">
          <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>{t('AppSettings_LoadFailed')}</Typography>
        </Paper>
      )}

      {!isLoading && !isLoadFailed && (
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {visibleGroups.length === 0 && (
            <Paper variant="outlined">
              <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                {t(changedOnly && !search.trim() ? nothingChangedKey : 'AppSettings_NoMatch')}
              </Typography>
            </Paper>
          )}

          {visibleGroups.map((group) => {
            const changed = changedIn(group.fields);

            return (
              <Paper
                key={group.id}
                variant="outlined"
                data-group={group.id}
                ref={(element: HTMLDivElement | null) => {
                  if (element) groupElements.current.set(group.id, element);
                  else groupElements.current.delete(group.id);
                }}
                sx={{ overflow: 'hidden' }}
              >
                <Stack direction="row" sx={groupHeaderStyles}>
                  <Box sx={tileStyles}>{groupIcons[group.id] ?? <SlidersHIcon />}</Box>
                  <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{t(groupLabelKey(group.id))}</Typography>
                  {changed > 0 && (
                    <Box component="span" sx={changedPillStyles}>{t('AppSettings_ChangedCount', [changed])}</Box>
                  )}
                </Stack>

                {sectionsOf(group.fields).map((run, index) => (
                  <Box
                    key={run.section ?? `standalone-${index}`}
                    sx={{ borderTop: index > 0 ? '1px solid' : 0, borderColor: 'divider' }}
                  >
                    {run.section && <Typography sx={sectionHeaderStyles}>{t(sectionLabelKey(run.section))}</Typography>}
                    {run.settings.map((definition) => {
                      const lineColorKey = lineColorKeyFor(definition.key);
                      const lineColorDefinition = definition.type === 'lineWidth' ? definitionByKey.get(lineColorKey) : undefined;

                      return (
                        <SettingRow
                          key={definition.key}
                          definition={definition}
                          resolved={resolve(definition)}
                          pending={definition.key in draft}
                          disabled={!canEdit}
                          dormantHint={dormantHint(definition)}
                          lineColor={lineColorDefinition ? String(resolve(lineColorDefinition).value) : undefined}
                          onChange={(value) => setValue(definition.key, value)}
                          onRevert={() => setValue(definition.key, null)}
                        />
                      );
                    })}
                  </Box>
                ))}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Nothing is written until Save: held in reach while anything is unsaved. */}
      {canEdit && pendingCount > 0 && (
        <Stack direction="row" sx={saveBarStyles}>
          <Box sx={pendingDotStyles} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{t('Customization_Designer_Unsaved')}</Typography>
          <Box component="span" sx={countPillStyles}>{pendingCount}</Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button color="inherit" size="small" disabled={isSaving} onClick={discard} sx={buttonStyles}>
            {t('Customization_Designer_Discard')}
          </Button>
          <Button variant="contained" size="small" disableElevation disabled={isSaving} onClick={save} sx={buttonStyles}>
            {t('Common_Save')}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default AppSettingsPage;
